<template>
  <div class="p-4">
    <div :id="elementId" class="mb-4"></div>
    <button
      type="button"
      @click="processPayment"
      :disabled="!canPay"
      class="btn rounded-full bg-echoes-primary hover:bg-echoes-primary/80 border-echoes-primary text-white w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
    >
      <svg
        v-if="isBusy"
        class="size-5 animate-spin text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" stroke="currentColor" stroke-width="4"></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <i v-else class="mdi mdi-credit-card"></i>
      {{ isBusy ? 'Processing...' : `Pay $${amountUsd}` }}
    </button>
    <p v-if="!isBusy && !isPaymentMethodComplete" class="mt-2 text-xs text-white/50 text-center">
      Select a payment method to continue
    </p>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, nextTick, computed } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { useSystemSettings } from '@/composables/use-system-settings';
import { useToast } from '@/composables/use-toast';
import { createApiCreditsTopUp } from '@/services/apiCreditsService.js';

const props = defineProps({
  packageId: {
    type: String,
    default: '',
  },
  amountUsd: {
    type: Number,
    required: true,
  },
  returnUrl: {
    type: String,
    default: '',
  },
  elementId: {
    type: String,
    default: 'api-credits-card-element',
  },
});

const emit = defineEmits(['payment-success', 'payment-error']);

const { getSystemSetting } = useSystemSettings();
const { addToast } = useToast();

const stripe = ref(null);
const elements = ref(null);
const paymentElement = ref(null);
const isCreatingPaymentIntent = ref(false);
const isConfirming = ref(false);
const isPaymentMethodComplete = ref(false);
const paymentIntentId = ref(null);

const isBusy = computed(() => isCreatingPaymentIntent.value || isConfirming.value);
const canPay = computed(
  () => !isBusy.value && isPaymentMethodComplete.value && !!elements.value,
);

const stripePromise = computed(() => {
  const stripePublicKey = getSystemSetting('STRIPE_PUBLIC_KEY');
  return stripePublicKey ? loadStripe(stripePublicKey) : null;
});

const handlePaymentElementChange = (event) => {
  isPaymentMethodComplete.value = Boolean(event?.complete);
};

const teardownPaymentElement = () => {
  if (paymentElement.value) {
    paymentElement.value.off?.('change', handlePaymentElementChange);
    try {
      paymentElement.value.unmount();
    } catch {
      // element may already be unmounted
    }
  }
  paymentElement.value = null;
  elements.value = null;
  paymentIntentId.value = null;
  isPaymentMethodComplete.value = false;
};

const initializeStripe = async () => {
  try {
    isCreatingPaymentIntent.value = true;
    teardownPaymentElement();
    if (!stripe.value) {
      stripe.value = await stripePromise.value;
    }
    if (!stripe.value) {
      throw new Error('Stripe public key is not configured');
    }

    const response = await createApiCreditsTopUp({
      packageId: props.packageId,
      amountUsd: props.amountUsd,
    });
    paymentIntentId.value = response.payment_intent_id;
    const clientSecret = response.client_secret;

    const savedTheme = localStorage.getItem('theme') || 'dark';
    elements.value = stripe.value.elements({
      clientSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#FE3E01',
          colorBackground: savedTheme === 'dark' ? '#232323' : '#ffffff',
          colorText: savedTheme === 'dark' ? '#ffffff' : '#424770',
          colorDanger: '#df1b41',
          borderRadius: '8px',
        },
      },
    });

    await nextTick();
    if (document.getElementById(props.elementId)) {
      paymentElement.value = elements.value.create('payment');
      paymentElement.value.on('change', handlePaymentElementChange);
      paymentElement.value.mount(`#${props.elementId}`);
    }
  } catch (error) {
    console.error(error);
    addToast(error.response?.data?.message || 'Error initializing payment. Please try again.', 'error');
    emit('payment-error', error);
  } finally {
    isCreatingPaymentIntent.value = false;
  }
};

const isUnexpectedPaymentIntentState = (error) => {
  const code = error?.code || error?.response?.data?.code;
  const declineCode = error?.decline_code;
  const message = String(error?.message || '');
  return (
    code === 'payment_intent_unexpected_state' ||
    declineCode === 'payment_intent_unexpected_state' ||
    message.includes('payment_intent_unexpected_state') ||
    message.includes('has already been confirmed') ||
    message.includes('unexpected state')
  );
};

const processPayment = async () => {
  if (!canPay.value) {
    addToast('Select a payment method to continue', 'error');
    return;
  }

  try {
    if (!stripe.value || !elements.value) {
      throw new Error('Stripe was not initialized correctly');
    }

    isConfirming.value = true;
    const { error: submitError } = await elements.value.submit();
    if (submitError) {
      throw submitError;
    }

    const returnUrl =
      props.returnUrl ||
      `${globalThis.location.origin}/creator-profile?page=api-credits&topup=1`;

    const result = await stripe.value.confirmPayment({
      elements: elements.value,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      throw result.error;
    }

    emit('payment-success', {
      payment_intent_id: paymentIntentId.value || result.paymentIntent?.id,
    });
  } catch (error) {
    console.error(error);
    if (isUnexpectedPaymentIntentState(error)) {
      addToast('That payment was already completed. Preparing a new checkout...', 'warning');
      await initializeStripe();
      emit('payment-error', error);
      return;
    }
    addToast('Error processing payment: ' + (error.message || 'Try again'), 'error');
    emit('payment-error', error);
  } finally {
    isConfirming.value = false;
  }
};

onMounted(() => {
  initializeStripe();
});

onBeforeUnmount(() => {
  teardownPaymentElement();
});
</script>
